import { useEffect, useState } from 'react';
import { API_URL, buildKioskAuthHeaders } from './api';
import './ProductContracts.css';

const SHOPS = {
  beresto: { name: 'BERESTO', origin: 'https://beresto.fr' },
  becaisse: { name: 'BECAISSE', origin: 'https://becaisse.fr' },
  beborne: { name: 'BEKIOSK', origin: 'https://beborne.com' },
  befid: { name: 'BEFID', origin: 'https://befid.fr' },
  beclick: { name: 'BECLICK', origin: 'https://beclick.fr' },
  beguest: { name: 'BEMenu', origin: 'https://bemenu.fr' },
};
const STATUSES = {
  pending: 'En attente', active: 'Actif', cancel_scheduled: 'Résiliation programmée', cancelled: 'Résilié',
  expired: 'Expiré', suspended: 'Suspendu', payment_pending: 'Paiement en attente', approval_pending: 'Approbation en attente',
  past_due: 'Échéance impayée', unpaid: 'Impayé', unknown: 'État inconnu',
};
const CYCLES = { monthly: 'Mensuel', annual: 'Annuel', unknown: 'Non renseigné' };
const ALLOCATIONS = { released: 'Libérée', recorded: 'Enregistrée', reserved: 'Réservée' };
const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
function isDate(value) {
  return value === null || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)
    && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === (value.length === 20 ? value.replace('Z', '.000Z') : value));
}
function validPayload(data) {
  return data?.schemaVersion === 1 && Array.isArray(data.contracts) && data.contracts.every(contract => contract
    && typeof contract.productId === 'string' && /^[a-z0-9_-]+$/.test(contract.productId)
    && typeof contract.name === 'string' && contract.name.trim() && has(SHOPS, contract.brand)
    && has(STATUSES, contract.status) && has(CYCLES, contract.billingCycle)
    && Number.isSafeInteger(contract.quantity) && contract.quantity >= 0 && isDate(contract.paidThrough)
    && typeof contract.cancelAtPeriodEnd === 'boolean' && has(ALLOCATIONS, contract.allocation)
    && contract.managementUrl === `${SHOPS[contract.brand].origin}/boutique.html`);
}
function periodDate(value) {
  return value === null ? 'Non renseignée' : new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris',
  }).format(new Date(value));
}

export default function ProductContracts() {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading' });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'loading' });
    async function load() {
      try {
        const response = await fetch(`${API_URL}/api/admin/options/contracts`, {
          method: 'GET', headers: buildKioskAuthHeaders(), signal: controller.signal,
        });
        if (!response.ok) throw new Error('unavailable');
        const data = await response.json();
        if (!validPayload(data)) throw new Error('invalid-contracts');
        if (active) setState({ status: 'loaded', data });
      } catch (_) { if (active) setState({ status: 'error' }); }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [attempt]);
  return <section className="product-contracts" aria-labelledby="product-contracts-title">
    <div className="product-contracts-heading"><span>Votre établissement</span><h2 id="product-contracts-title">Abonnements boutique</h2>
      <p>Les contrats reliés à votre établissement et leur état enregistré. Vos droits d’accès restent indiqués dans les outils ci-dessus.</p></div>
    {state.status === 'loading' && <p role="status">Chargement des abonnements…</p>}
    {state.status === 'error' && <div className="product-contracts-message" role="alert">
      <p>Les abonnements boutique sont momentanément indisponibles. Vos accès et les offres restent consultables.</p>
      <button type="button" onClick={() => setAttempt(value => value + 1)}>Réessayer les abonnements</button>
    </div>}
    {state.status === 'loaded' && <>
      {state.data.contracts.length === 0 ? <div className="product-contracts-message">
        <p>Aucun contrat boutique relié dans cet inventaire</p>
        <p>Les accès accordés manuellement et certains contrats historiques ne figurent pas dans cet inventaire.</p>
      </div> : <>
        <div className="product-contracts-grid">{state.data.contracts.map((contract, index) => <article className="product-contract" key={`${contract.productId}-${index}`} aria-label={`Abonnement ${contract.name}`}>
          <span className="product-contract-brand">Boutique {SHOPS[contract.brand].name}</span><h3>{contract.name}</h3>
          <dl>
            <div><dt>État enregistré</dt><dd>{STATUSES[contract.status]}</dd></div>
            <div><dt>Quantité pour cet établissement</dt><dd>{contract.quantity}</dd></div>
            <div><dt>Cycle</dt><dd>{CYCLES[contract.billingCycle]}</dd></div>
            <div><dt>Fin de période enregistrée</dt><dd>{periodDate(contract.paidThrough)}</dd></div>
            <div><dt>Attribution à l’établissement</dt><dd>{ALLOCATIONS[contract.allocation]}</dd></div>
          </dl>
          {contract.cancelAtPeriodEnd && <p className="product-contract-cancellation">Résiliation programmée en fin de période.</p>}
          <a href={contract.managementUrl} target="_blank" rel="noopener noreferrer">Ouvrir la boutique {SHOPS[contract.brand].name} <span aria-hidden="true">↗</span><span className="product-contracts-sr-only"> (nouvel onglet)</span></a>
        </article>)}</div>
        <p className="product-contracts-note">Ces informations reflètent l’état enregistré et ne confirment pas un paiement en direct ni un renouvellement futur. L’attribution d’un contrat et le droit d’utiliser un outil sont distincts.</p>
        <p className="product-contracts-note">La boutique utilise un compte distinct de votre session administrateur.</p>
      </>}
    </>}
  </section>;
}
